import { ApiAttribute, EndpointAttributes } from '@context/openapi/types';
import { CodeSample } from '@/services/postmanCollection/postman_util';

export type SampleRequestKeyValue = {
  key: string;
  value: string | number;
  index?: number;
};

export const convertCodeSampleToEndpointAttributes = (
  method: string,
  endpointUrl: string,
  endpointAttributes: EndpointAttributes,
  codeSample?: CodeSample
) => {
  if (!codeSample) return;
  const {
    multiAttributes,
    objectAttributes,
    arrayAttributes,
    simpleAttributes,
  } = sieveSampleRequestMap(codeSample.params.map);
  const endpointAttrs =
    method == 'post'
      ? endpointAttributes.params
      : endpointAttributes.queryParams;
  if (simpleAttributes) {
    updateAttributes(endpointAttrs, simpleAttributes);
  }
  if (objectAttributes) {
    if (method == 'post') {
      updateObjectAttributes(endpointAttrs, objectAttributes);
    } else {
      updateFilterAttributes(endpointAttrs, objectAttributes);
    }
  }
  if (arrayAttributes || multiAttributes) {
    updateArrayAttributes(endpointAttrs, [
      ...arrayAttributes,
      ...multiAttributes,
    ]);
  }
  if (endpointAttributes.pathParameters) {
    updatePathParams(
      endpointUrl,
      codeSample.uri,
      endpointAttributes.pathParameters
    );
  }
  return endpointAttributes;
};

export function sieveSampleRequestMap(codeSamplePayload: {
  [key: string]: string;
}) {
  const multiAttributes: SampleRequestKeyValue[] = [];
  const arrayAttributes: SampleRequestKeyValue[] = [];
  const objectAttributes: SampleRequestKeyValue[] = [];
  const simpleAttributes: SampleRequestKeyValue[] = [];

  Object.entries(codeSamplePayload).forEach(([key, value]) => {
    const keys = key.replace(/\[([^\]]+)]/g, '.$1').split('.');

    const hasArrayIndex = keys.some((item) => item.match(/^\d+$/));

    if (hasArrayIndex && keys.length === 3) {
      const index = Number(keys.find((item) => item.match(/^\d+$/)));
      const key = `${keys[0]}[${index}].${keys[1]}`;
      multiAttributes.push({ key: key, value: value, index: index });
    } else if (keys.length === 3) {
      const convertedKey = keys.join('.');
      objectAttributes.push({ key: convertedKey, value: value });
    } else if (keys.length === 2) {
      if (keys.some((item) => item.match(/^\d+$/))) {
        const index = Number(keys[1]);
        const key = `${keys[0]}[${index}]`;
        arrayAttributes.push({ key: key, value: value, index: index });
      } else {
        const key = `${keys[0]}.${keys[1]}`;
        objectAttributes.push({ key: key, value: value });
      }
    } else {
      simpleAttributes.push({ key: key, value: value });
    }
  });

  return {
    multiAttributes,
    objectAttributes,
    arrayAttributes,
    simpleAttributes,
  };
}

export function getAttribute(
  endpointAttributes: ApiAttribute[],
  attr: SampleRequestKeyValue,
  isArray: boolean = false
) {
  if (!isArray) {
    return endpointAttributes.find(
      (endpointAttribute) => endpointAttribute.key === attr.key
    );
  }
  const parentAttr = endpointAttributes.find(
    (endpointAttribute) => endpointAttribute.key === attr.key.split('[')[0]
  );
  return parentAttr?.child.find(
    (childAttribute) => childAttribute.key === attr.key
  );
}

function updateAttributes(
  endpointAttributes: ApiAttribute[],
  attributes: SampleRequestKeyValue[]
) {
  attributes.map((attribute) => {
    const attr = getAttribute(endpointAttributes, attribute);
    if (attr) {
      attr.value = attribute.value;
    }
  });
}

function updateObjectAttributes(
  endpointAttributes: ApiAttribute[],
  attributes: SampleRequestKeyValue[]
) {
  attributes.map((attribute) => {
    const keys = attribute.key.split('.');

    if (keys.length === 2) {
      const parentAttr = endpointAttributes.find(
        (endpointAttribute) => endpointAttribute.key === keys[0]
      );
      const attr = parentAttr?.child.find(
        (child) => child.key === attribute.key
      );
      if (attr) {
        attr.value = attribute.value;
      }
      return;
    }

    //Handle 3-level nested filters: invoice.status.is_not
    //The endpointAttributes for a target like invoice.status.is_not looks like invoice -> invoice.status -> is_not
    //So we should identify the second layer as invoice.status and not as just status
    if (keys.length === 3) {
      // Find parent object: "invoice"
      const parentAttr = endpointAttributes.find(
        (endpointAttribute) => endpointAttribute.key === keys[0]
      );

      if (parentAttr && parentAttr.child) {
        // Identifies the child filter field as "invoice.status" and not as "status"
        const filterField = parentAttr.child.find(
          (child) => child.key === `${keys[0]}.${keys[1]}`
        );

        if (filterField && filterField.type === 'FILTER') {
          filterField.filter = keys[2]; // "is_not"
          filterField.value = attribute.value; // "paid"
        }
      }
    }
  });
}

function updateFilterAttributes(
  endpointAttributes: ApiAttribute[],
  attributes: SampleRequestKeyValue[]
) {
  attributes.map((attribute) => {
    const keys = attribute.key.split('.');
    const attrKey = keys[0];
    const filterType = keys[1];
    const attr: ApiAttribute | undefined = endpointAttributes.find(
      (endpointAttribute) => endpointAttribute.key === attrKey
    );
    if (attr) {
      attr.value = attribute.value;
      attr.filter = filterType;
    }
  });
}

function childItemLength(field: ApiAttribute) {
  // Add defensive checks
  if (
    !field.child ||
    !field.defaultArrayElement ||
    field.defaultArrayElement.length === 0
  ) {
    return 0;
  }

  return field.child.length / field.defaultArrayElement!.length;
}

function addArrayItem(field: ApiAttribute) {
  // Add defensive check before processing
  if (!field.defaultArrayElement || field.defaultArrayElement.length === 0) {
    return;
  }

  // Initialize child array if it doesn't exist
  if (!field.child) {
    field.child = [];
  }

  const newArrayIndex = childItemLength(field);
  const keyIndex = `[${newArrayIndex}]`;
  const newItem: ApiAttribute[] =
    field.defaultArrayElement?.map((child) => {
      return {
        ...child,
        key: child.key.replace(/\[(\d+)\]/g, keyIndex),
        arrayIndex: newArrayIndex,
        value: '',
      };
    }) ?? [];
  newItem?.map((child) => {
    field.child.push(child);
  });
}

function updateArrayAttributes(
  endpointAttributes: ApiAttribute[],
  attributes: SampleRequestKeyValue[]
) {
  attributes.map((attribute) => {
    const arrayAttr = getAttribute(endpointAttributes, attribute, true);
    if (arrayAttr) {
      arrayAttr.value = attribute.value;
    } else {
      // get parent key
      const parentAttr = endpointAttributes.find(
        (endpointAttribute) =>
          attribute.key.split('[')[0] === endpointAttribute.name
      );
      if (parentAttr) {
        // add new child to parent
        addArrayItem(parentAttr);
        // get newly added child
        const newArrayAttr = getAttribute(endpointAttributes, attribute, true);
        if (newArrayAttr) {
          newArrayAttr.value = attribute.value;
        }
      }
    }
  });
}

const updatePathParams = (
  endpointUrl: string,
  codeSampleUrl: string,
  pathParams: ApiAttribute[]
) => {
  const splitUrl = (url: string): string[] =>
    url.replace(/[{}]/g, '').split('/').filter(Boolean);

  const endpointSegments = splitUrl(endpointUrl);
  const codeSampleSegments = splitUrl(codeSampleUrl);

  pathParams.map((param) => {
    const index = endpointSegments.findIndex(
      (segment) => segment === param.name
    );
    if (index !== -1 && codeSampleSegments[index] != param.name) {
      param.value = codeSampleSegments[index];
    }
  });
};
